/**
 * PermissionsTab - Onglet "Permissions" : grille de gestion des permissions
 * pour un rôle, organisées par catégorie. Chaque permission est associée à
 * un scope (Aucun / Établissement / Organisation / Compte / Global).
 *
 * Le scope "Global" n'est exposé que pour les rôles système (le backend
 * filtrera de toute façon).
 *
 * Les rôles système sont en lecture seule.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CheckIcon, CloseIcon, SearchIcon } from '../../icons';
import {
  PERMISSIONS,
  PERMISSION_CATEGORIES,
  SCOPE_LABELS,
  Scope
} from '../../shared/permissions';
import {
  RoleDetail,
  RolePermission,
  API_BASE,
  authHeaders
} from './types';

interface PermissionsTabProps {
  role: RoleDetail;
  onSaved: () => void;
}

// Icône cadenas (SVG inline simple) — pour signaler le mode lecture seule
const LockIcon: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Scopes affichés dans l'UI (ordre fixe)
const ALL_SCOPES: Scope[] = ['establishment', 'organization', 'account', 'global'];

// Permissions qui n'ont d'effet qu'au scope global (super_admin). Leur sélecteur
// de scope est grisé pour les rôles non-système : aucun scope proposé ici n'a d'effet.
const GLOBAL_ONLY_PERMS = new Set<string>(['roles.manage']);

function PermissionsTab({ role, onSaved }: PermissionsTabProps) {
  const [grants, setGrants] = useState<Record<string, Scope>>({});
  const [initialGrants, setInitialGrants] = useState<Record<string, Scope>>({});
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isReadOnly = role.is_system;

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`[PermissionsTab] Fetching /api/roles/${role.id}/permissions`);
      const res = await fetch(`${API_BASE}/api/roles/${role.id}/permissions`, {
        headers: authHeaders()
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      const map: Record<string, Scope> = {};
      (json.permissions as RolePermission[] || []).forEach(p => {
        map[p.permission] = p.scope;
      });
      setGrants(map);
      setInitialGrants(map);
      console.log(`[PermissionsTab] ${Object.keys(map).length} permission(s) actives`);
    } catch (err) {
      console.error('[PermissionsTab] Erreur fetch:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [role.id]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // dirty
  const isDirty = useMemo(() => {
    const keys = new Set([...Object.keys(grants), ...Object.keys(initialGrants)]);
    for (const k of keys) {
      if (grants[k] !== initialGrants[k]) return true;
    }
    return false;
  }, [grants, initialGrants]);

  // Filtrage par recherche
  const filteredPermissions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PERMISSIONS;
    return PERMISSIONS.filter(p =>
      p.id.toLowerCase().includes(q) ||
      p.label.toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q)
    );
  }, [search]);

  // Groupe par catégorie (en respectant l'ordre PERMISSION_CATEGORIES)
  const grouped = useMemo(() => {
    return PERMISSION_CATEGORIES.map(cat => ({
      cat,
      perms: filteredPermissions.filter(p => p.category === cat.id)
    })).filter(g => g.perms.length > 0);
  }, [filteredPermissions]);

  function setScope(permId: string, scope: Scope | null) {
    if (isReadOnly) return;
    if (GLOBAL_ONLY_PERMS.has(permId) && !role.is_system) return;
    setGrants(prev => {
      const next = { ...prev };
      if (scope === null) {
        delete next[permId];
      } else {
        next[permId] = scope;
      }
      return next;
    });
  }

  async function handleSave() {
    if (!isDirty || isReadOnly) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        permissions: Object.entries(grants).map(([permission, scope]) => ({ permission, scope }))
      };
      console.log(`[PermissionsTab] PUT /api/roles/${role.id}/permissions — ${payload.permissions.length} permission(s)`);
      const res = await fetch(`${API_BASE}/api/roles/${role.id}/permissions`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json?.error?.message || `Erreur HTTP ${res.status}`);
      }
      console.log('[PermissionsTab] Permissions enregistrées');
      setSuccess('Permissions enregistrées.');
      setTimeout(() => setSuccess(null), 3000);
      onSaved();
      // Re-sync local state avec ce qui a été enregistré
      setInitialGrants(grants);
    } catch (err) {
      console.error('[PermissionsTab] Erreur save:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  }

  function toggleCategory(catId: string) {
    setCollapsed(prev => ({ ...prev, [catId]: !prev[catId] }));
  }

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
        Chargement des permissions...
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {isReadOnly && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          backgroundColor: '#fef3c7',
          color: '#92400e',
          border: '1px solid #fcd34d',
          borderRadius: '6px',
          fontSize: '13px'
        }}>
          <LockIcon size={14} color="#92400e" />
          Ce rôle système n'est pas modifiable. Ses permissions sont gérées par l'application.
        </div>
      )}

      {/* Recherche */}
      <div style={{ position: 'relative' }}>
        <SearchIcon
          size={14}
          color="#94a3b8"
          style={{
            position: 'absolute',
            left: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none'
          }}
        />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher une permission..."
          style={{
            width: '100%',
            padding: '8px 10px 8px 32px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Catégories */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {grouped.length === 0 && (
          <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
            Aucune permission ne correspond à la recherche.
          </div>
        )}
        {grouped.map(({ cat, perms }) => {
          const isCollapsed = collapsed[cat.id];
          const grantedInCat = perms.filter(p => grants[p.id]).length;
          return (
            <div key={cat.id} style={{
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <button
                type="button"
                onClick={() => toggleCategory(cat.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  backgroundColor: '#f8fafc',
                  border: 'none',
                  borderBottom: isCollapsed ? 'none' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                <span style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1e293b'
                }}>
                  {cat.label}
                </span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  {grantedInCat} / {perms.length} {isCollapsed ? '▸' : '▾'}
                </span>
              </button>

              {!isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {perms.map((perm, idx) => {
                    const currentScope = grants[perm.id];
                    const permLocked = GLOBAL_ONLY_PERMS.has(perm.id) && !role.is_system;
                    return (
                      <div
                        key={perm.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 14px',
                          borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none',
                          flexWrap: 'wrap'
                        }}
                      >
                        <div style={{ flex: 1, minWidth: '240px' }}>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#1e293b'
                          }}>
                            {perm.label}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#64748b',
                            marginTop: '2px'
                          }}>
                            {perm.description}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#94a3b8',
                            marginTop: '2px',
                            fontFamily: 'monospace'
                          }}>
                            {perm.id}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '4px',
                          flexWrap: 'wrap',
                          alignItems: 'center'
                        }}>
                          {permLocked && (
                            <span style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic', marginRight: '4px' }}>
                              Réservé aux super-administrateurs
                            </span>
                          )}
                          {/* Bouton "Aucun" */}
                          <ScopeButton
                            label="Aucun"
                            isSelected={!currentScope}
                            disabled={isReadOnly || permLocked}
                            onClick={() => setScope(perm.id, null)}
                            tone="neutral"
                          />
                          {ALL_SCOPES.map(scope => {
                            // Cacher Global pour les rôles non-système (filet de sécurité côté UI)
                            if (scope === 'global' && !role.is_system) return null;
                            const selected = currentScope === scope;
                            return (
                              <ScopeButton
                                key={scope}
                                label={SCOPE_LABELS[scope]}
                                isSelected={selected}
                                disabled={isReadOnly || permLocked}
                                onClick={() => setScope(perm.id, scope)}
                                tone="action"
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          backgroundColor: '#fef2f2',
          color: '#991b1b',
          border: '1px solid #fca5a5',
          borderRadius: '6px',
          fontSize: '13px'
        }}>
          <CloseIcon size={14} />
          {error}
        </div>
      )}
      {success && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          backgroundColor: '#dcfce7',
          color: '#166534',
          border: '1px solid #86efac',
          borderRadius: '6px',
          fontSize: '13px'
        }}>
          <CheckIcon size={14} />
          {success}
        </div>
      )}

      {/* Footer save */}
      {!isReadOnly && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '12px 0',
          borderTop: '1px solid #e2e8f0',
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white'
        }}>
          {isDirty && (
            <span style={{
              fontSize: '12px',
              padding: '3px 10px',
              backgroundColor: '#fff7ed',
              color: '#c2410c',
              border: '1px solid #fdba74',
              borderRadius: '20px',
              fontWeight: 500
            }}>
              Modifié
            </span>
          )}
          <button
            type="button"
            onClick={() => { setGrants(initialGrants); setError(null); setSuccess(null); }}
            disabled={!isDirty || saving}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: !isDirty || saving ? 'not-allowed' : 'pointer',
              opacity: !isDirty || saving ? 0.5 : 1
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving}
            style={{
              padding: '8px 16px',
              backgroundColor: !isDirty || saving ? '#93c5fd' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: !isDirty || saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      )}
    </div>
  );
}

interface ScopeButtonProps {
  label: string;
  isSelected: boolean;
  disabled: boolean;
  onClick: () => void;
  tone: 'neutral' | 'action';
}

function ScopeButton({ label, isSelected, disabled, onClick, tone }: ScopeButtonProps) {
  const baseColor = tone === 'neutral' ? '#64748b' : '#3b82f6';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '5px 10px',
        fontSize: '12px',
        fontWeight: 500,
        border: isSelected ? `1px solid ${baseColor}` : '1px solid #e2e8f0',
        backgroundColor: isSelected ? baseColor : 'white',
        color: isSelected ? 'white' : '#475569',
        borderRadius: '6px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </button>
  );
}

export default PermissionsTab;
